import { computed, getCurrentInstance, nextTick, onBeforeUnmount, reactive, watch } from 'vue'

/**
 * The one paged list in the marketing section.
 *
 * Every picker in the campaign builder needs the same things and they are all
 * easy to get subtly wrong, which is why they are written once:
 *
 *   1. keyset paging      - a cursor, never an offset, so a row leaving the set
 *                           mid-scroll cannot make the next page skip somebody;
 *   2. one generation     - a query supersedes everything the previous one left
 *                           behind: its request, its timer and its cursor;
 *   3. one debounce       - the search box asks the server when typing pauses,
 *                           not per keystroke;
 *   4. an observer        - the next page is fetched a few rows before the end
 *                           of the loaded ones, rooted on the list that scrolls;
 *   5. failure that keeps - a refused page leaves the rows already on screen and
 *                           offers a retry; only a first page has nothing to keep;
 *   6. teardown           - timer, observer and request all stop at unmount, and
 *                           nothing that was in flight may write afterwards.
 *
 * What it deliberately does NOT own is what the rows mean. Selection, labels,
 * markup and the shape of the request beyond `search`/`cursor` belong to the
 * component: this is the engine, not the widget.
 *
 * The server contract is one shape for every endpoint that feeds one of these:
 *
 *   { "items": [ ... ], "next_cursor": "..." | null }
 *
 * Usage, from setup():
 *
 *   const rows = usePagedList({ url, method: 'POST', params: () => ({ ... }) })
 *   // template: <ul :ref="rows.observe"> ... </ul>
 *
 * The file is .mjs so Node can load it directly: tests/js/paged-list.test.mjs
 * exercises this module with the runtime's own test runner and no build step.
 */

/** Milliseconds of quiet before a search reaches the server. */
export const LIST_DEBOUNCE = 250

/**
 * How far from the end of the loaded rows the next page is fetched: far enough
 * that it arrives before the reader gets there, close enough that idly nudging
 * a list does not pull pages nobody looks at.
 */
export const TRIGGER_ROWS_FROM_END = 10

/**
 * @param {Object} options
 * @param {string} options.url            Endpoint returning { items, next_cursor }.
 * @param {'GET'|'POST'} [options.method] GET sends search/cursor as query
 *                                        parameters, POST as a JSON body.
 * @param {function(): Object} [options.params] Extra parameters per request -
 *                                        read at request time, so it always
 *                                        sees the caller's current state.
 * @return {Object} A reactive controller. State: items, search, loading,
 *                  error, cursor, isEmpty, triggerIndex, needsManualLoad.
 *                  Operations: reload, reloadSoon, loadMore, observe, destroy.
 */
export function usePagedList({ url, method = 'GET', params = () => ({}) }) {
  const canObserve = typeof window !== 'undefined' && 'IntersectionObserver' in window

  // Deliberately not reactive: nothing renders from them, and a re-render on
  // every abort would be noise.
  let request = null
  let timer = null
  let observer = null
  let listEl = null

  /**
   * Which query the rows on screen belong to.
   *
   * An AbortController settles which REQUEST wins; this settles which QUERY
   * does, and they are not the same thing. A debounced reload has no request to
   * abort yet, but from the moment it is scheduled the cursor on screen belongs
   * to the outgoing query and must not be paged with. Everything that can
   * outlive a query - a pending timer, an in-flight response, a cursor, a
   * teardown - is checked against this number.
   */
  let generation = 0

  /** True between a debounced reload being scheduled and it starting. */
  let reloadPending = false

  const list = reactive({
    items: [],
    search: '',
    loading: false,
    error: '',
    /** The next page's cursor, or null when there is nothing left to load. */
    cursor: null,

    /** Which loaded row carries the marker the observer watches. */
    triggerIndex: computed(() => Math.max(0, list.items.length - TRIGGER_ROWS_FROM_END)),

    /**
     * Whether the footer has to offer a button instead of waiting for a scroll.
     * A page that failed left the observer disconnected on purpose - its
     * trigger row is still in view, so re-arming would retry by itself and keep
     * retrying - and a browser without IntersectionObserver never had a trigger.
     */
    needsManualLoad: computed(() => !!list.cursor && (!canObserve || !!list.error)),

    /** A request has finished, without an error, and produced nothing. */
    isEmpty: computed(() => !list.loading && !list.error && !list.items.length),

    reload,
    reloadSoon,
    loadMore,
    observe,
    destroy
  })

  /**
   * @param {?string} cursor Null for the first page, which replaces the list.
   * @return {Promise<void>}
   */
  async function fetchPage(cursor) {
    const append = !!cursor
    const era = generation

    // The debounce limits how many requests are sent, never the order they come
    // back in. This is what settles that order: there is only ever one.
    request?.abort()
    request = new AbortController()
    const own = request

    list.loading = true
    list.error = ''

    try {
      const payload = { ...params(), search: list.search || '', cursor: cursor || null }
      const data = await requestJson(url, method, payload, own.signal)

      // Belt to the abort's braces. An abort is not guaranteed to land before a
      // response that was already resolving, and destroy() has no request left
      // to abort at all - both show up here as an answer to a question nobody
      // is asking any more.
      if (era !== generation) {
        return
      }

      list.items = append ? merge(list.items, data.items) : (data.items || [])
      list.cursor = data.next_cursor || null

      if (!append) {
        // Back to the top before the observer is armed - nextTick callbacks run
        // in the order they were queued. Left at the bottom of the previous
        // result, the new trigger row would already be in view and every search
        // would pull a second page nobody asked for.
        nextTick(() => {
          if (listEl) {
            listEl.scrollTop = 0
          }
        })
      }

      arm()
    } catch (e) {
      // An abort is this controller replacing its own request, not a failure.
      if (e.name === 'AbortError' || era !== generation) {
        return
      }

      // The server's own sentence when it answered, a plain one when it did
      // not: "Failed to fetch" is a browser string, not client copy.
      fail(append, e.fromServer ? e.message : 'Could not reach the server.')
    } finally {
      // Only the newest request owns the spinner: an aborted one's finally would
      // switch it off while its replacement is still running, and the list would
      // sit there looking finished.
      if (own === request) {
        list.loading = false
      }
    }
  }

  /**
   * Rows the client is already looking at survive a page that did not arrive -
   * some of them may be ticked. Only a first page has nothing to keep.
   *
   * @param {boolean} append
   * @param {string} message
   */
  function fail(append, message) {
    list.error = message
    observer?.disconnect()

    if (!append) {
      list.items = []
      list.cursor = null
    }
  }

  /** Start again from the first page, discarding what is loaded. */
  function reload() {
    clearTimeout(timer)
    generation++
    reloadPending = false
    fetchPage(null)
  }

  /**
   * The same, once the caller stops changing their mind.
   *
   * The generation moves NOW rather than when the timer fires: from this moment
   * the rows and the cursor on screen answer a question that has been withdrawn.
   */
  function reloadSoon() {
    clearTimeout(timer)
    generation++
    reloadPending = true
    request?.abort()

    timer = setTimeout(() => {
      reloadPending = false
      fetchPage(null)
    }, LIST_DEBOUNCE)
  }

  /**
   * Fetch the next page onto the end.
   *
   * Guarded on the request in flight rather than debounced: the observer fires
   * once per crossing, and this is what stops it firing again while the answer
   * is on its way. Guarded on the pending reload too - a cursor belongs to the
   * query that produced it, and during the debounce the one on screen belongs
   * to the query being replaced.
   */
  function loadMore() {
    if (reloadPending || list.loading || !list.cursor) {
      return
    }

    fetchPage(list.cursor)
  }

  /**
   * The scrolling element, as a template ref: `<ul :ref="list.observe">`.
   *
   * Vue calls it with the element when it appears and with null when it goes,
   * which is exactly when the observer has to be built and thrown away - an
   * observer's root is fixed at construction, so a new element means a new one.
   * Called again with the same element it does nothing, so a re-render cannot
   * pile observers up on one list.
   *
   * @param {?Element} el
   */
  function observe(el) {
    const next = el || null

    if (listEl === next) {
      return
    }

    // The old element's observer, before the new element gets one. Never two.
    observer?.disconnect()
    observer = null
    listEl = next

    arm()
  }

  /**
   * Point the observer at whichever row now carries the marker - a different
   * row after every page, so one left watching the old row would never fire
   * again.
   */
  function arm() {
    if (!canObserve || !listEl) {
      return
    }

    const era = generation

    nextTick(() => {
      // The list may have been replaced, torn down or superseded while this
      // waited for the DOM.
      if (era !== generation || !listEl) {
        return
      }

      observer?.disconnect()

      const trigger = listEl.querySelector('[data-load-more]')

      // Nothing left to load: leave it disconnected rather than watching a row
      // whose crossing would be answered with a no-op.
      if (!trigger || !list.cursor) {
        return
      }

      if (!observer) {
        observer = new IntersectionObserver(
          (entries) => entries.some((entry) => entry.isIntersecting) && loadMore(),
          { root: listEl }
        )
      }

      observer.observe(trigger)
    })
  }

  /**
   * Everything this controller started, stopped - and nothing it started may
   * write to the list afterwards.
   */
  function destroy() {
    clearTimeout(timer)
    observer?.disconnect()
    request?.abort()

    // Anything still resolving belongs to a generation that no longer exists.
    generation++
    reloadPending = false
    observer = null
    request = null
    listEl = null

    // The aborted request's `finally` will not clear this: it checks that it is
    // still the current one, and it no longer is. A panel that is closed and
    // reopened would otherwise come back saying "Loading more…".
    list.loading = false
  }

  // Typing is the one input the controller owns outright, so it owns the reload
  // too - no component has to remember to wire it up.
  watch(() => list.search, reloadSoon)

  // Only inside a component, which is where the lists live. Outside one - a
  // test - the caller calls destroy() itself, and Vue is not asked to register
  // a hook on an instance that does not exist.
  if (getCurrentInstance()) {
    onBeforeUnmount(destroy)
  }

  return list
}

/**
 * Append a page without letting a row appear twice.
 *
 * Keyset paging does not repeat rows on its own, but nothing about a list
 * assembled from several requests guarantees that - and a duplicate id in a
 * checkbox list is a row that cannot be un-ticked.
 *
 * A repeat keeps its POSITION from the first page it appeared on and takes its
 * DATA from the last: a row must not jump under a cursor that is about to click
 * it, and where the two copies disagree the later read is the fresher one. A
 * Map does exactly that - `set` on an existing key replaces the value and
 * leaves the insertion order alone - which is why this is a Map and not a Set
 * of ids with a filter.
 *
 * @param {Object[]} loaded
 * @param {Object[]} page
 * @return {Object[]}
 */
export function merge(loaded, page) {
  const byId = new Map(loaded.map((item) => [item.id, item]))

  ;(page || []).forEach((item) => byId.set(item.id, item))

  return [...byId.values()]
}

/**
 * One same-origin JSON request, the way this application makes them.
 *
 * The CSRF token is read here rather than cached: bootstrap.js rewrites that
 * meta tag after a 419, and a copy taken earlier would be the expired one.
 *
 * @param {string} url
 * @param {'GET'|'POST'} method
 * @param {Object} payload
 * @param {AbortSignal} [signal]
 * @return {Promise<Object>}
 */
export async function requestJson(url, method, payload, signal) {
  const headers = {
    'X-Requested-With': 'XMLHttpRequest',
    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  let target = url
  let body

  if (method === 'GET') {
    const query = new URLSearchParams()

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== null && value !== '' && value !== undefined) {
        query.set(key, value)
      }
    })

    target = query.toString() ? `${url}?${query}` : url
  } else {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(payload)
  }

  const response = await fetch(target, { method, signal, headers, body })
  const data = await response.json().catch(() => ({}))

  if (!response.ok) {
    // The server is the authority on what it will accept; its sentence is
    // written for the client, so it is the one that gets shown.
    const refusal = new Error(data.message || 'The server refused that request.')
    refusal.fromServer = true

    throw refusal
  }

  return data
}
